import { useState, useMemo, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { dateToAAAAMMJJ, getDuration, formatTime } from "../../../_helpers/functions";
import { getInterventions, downloadInterventions } from "../../../_api/Intervention";
import { getInterventionsCategories } from "../../../_api/InterventionCategorie";
import { getUsers } from "../../../_api/Admin";
import { toast } from "react-hot-toast";
import { Filter } from "../../../_helpers/components/Forms/Filter";
import { Button } from "../../../_helpers/components/Button";
import { cssColor, htmlColor } from "./InterventionsColors";

const randomColors = [
    "oklch(70.2% 0.183 293.541)",
    "oklch(79.2% 0.209 151.711)",
    "oklch(85.2% 0.199 91.936)",
    "oklch(70.4% 0.191 22.216)",
    "oklch(75% 0.183 55.934)",
    "oklch(71.2% 0.194 13.428)",
    "oklch(78.9% 0.154 211.53)",
    "oklch(77.7% 0.152 181.912)",
    "oklch(82.8% 0.189 84.429)",
    "oklch(70.4% 0.04 256.788)",
    "oklch(76.5% 0.177 163.223)",
    "oklch(74.6% 0.16 232.661)",
    "oklch(71.8% 0.202 349.761)",
    "oklch(84.1% 0.238 128.85)",
    "oklch(71.4% 0.203 305.504)",
    "oklch(67.3% 0.182 276.935)",
    "oklch(70.7% 0.165 254.624)",
    "oklch(74% 0.238 322.16)",
]

export const InterventionsStats = () => {
    const [user, setUser, currentApp, currentMenu] = useOutletContext();
    const [loading, setLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [usersCreator, setUsersCreator] = useState([]);
    const [usersEnroll, setUsersEnroll] = useState([]);
    const [categories, setCategories] = useState([]);
    const [families, setFamilies] = useState([]);
    const [orderBy, setOrderBy] = useState("");
    const [display, setDisplay] = useState("GRAPH");

    // Période sélectionnée - semaine en cours par défaut
    const [startDate, setStartDate] = useState(dateToAAAAMMJJ(new Date(new Date().setMonth(0, 1)), "-"));
    const [endDate, setEndDate] = useState(dateToAAAAMMJJ(new Date(new Date().setMonth(12, 0)), "-"));

    // Type de répartition
    const [groupBy, setGroupBy] = useState("priority");

    // Affichage des interventions
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 15;

    const [filter, setFilter] = useState({ status: "ALL", priority: "ALL", team: "-1", family: "ALL", category: "-1", requester: "-1", performer: "-1" });

    const availableTeams = user.teams.sort((a, b) => (a.name < b.name ? -1 : 1));

    const priority = {
        ALL: {
            label: "Toutes",
            color: cssColor.ALL,
            piecolor: htmlColor.ALL, // slate-400
            order: 0,
        },
        URGENT: {
            label: "Urgente",
            color: cssColor.URGENT,
            piecolor: htmlColor.URGENT,
            order: 1,
        },
        IMPORTANT: {
            label: "Importante",
            color: cssColor.IMPORTANT,
            piecolor: htmlColor.IMPORTANT,
            order: 2,
        },
        NORMALE: {
            label: "Normale",
            color: cssColor.NORMALE,
            piecolor: htmlColor.NORMALE,
            order: 3,
        },
        MEMO: {
            label: "Pour\u00a0mémo",
            color: cssColor.MEMO,
            piecolor: htmlColor.MEMO,
            order: 4,
        },
    }

    const status = {
        ALL: {
            label: "Tous",
            color: cssColor.ALL,
            piecolor: htmlColor.ALL,
            order: 0,
        },
        OPEN: {
            label: "Ouvert",
            color: cssColor.OPEN,
            piecolor: htmlColor.OPEN,
            order: 1,
        },
        ONGOING: {
            label: "En cours",
            color: cssColor.ONGOING,
            piecolor: htmlColor.ONGOING,
            order: 2,
        },
        PENDING: {
            label: "En attente",
            color: cssColor.PENDING,
            piecolor: htmlColor.PENDING,
            order: 3,
        },
        CLOSED: {
            label: "Terminé",
            color: cssColor.CLOSED,
            piecolor: htmlColor.CLOSED,
            order: 4,
        },
        UNREALIZABLE: {
            label: "Non réalisable",
            color: cssColor.UNREALIZABLE,
            piecolor: htmlColor.UNREALIZABLE,
            order: 5,
        },
    }

    const groupByLabel = {
        priority: "priorité",
        team: "service",
        family: "groupe",
        category: "catégorie",
        requester: "demandeur",
        performer: "exécutant",
        status: "statut",
    }

    // 🔹 Charger les tickets
    const fetchTickets = async () => {
        setLoading(true);
        try {
            const resp = await getInterventions();
            setTickets(resp.data);
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des interventions.");
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Charger les catégories
    const fetchCategories = async () => {
        setLoading(true);
        try {
            const resp = await getInterventionsCategories();
            setCategories(resp.data);
            setFamilies(Array.from(new Set(resp.data.map((c) => {return c.family}))));
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des catégories d'interventions.");
        } finally {
            setLoading(false);
        }
    };

    // Chargement des users
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const resp = await getUsers();
            setUsersCreator(resp.data.filter((usr) => usr.policy.rights[currentApp]["TASK_LIST"]["sub_menu"]["MAIN"]["features"]["task_create"] === true))
            setUsersEnroll(resp.data.filter((usr) => usr.policy.rights[currentApp]["TASK_LIST"]["sub_menu"]["MAIN"]["features"]["task_enroll"] === true))
        } catch (err) {
            console.log(err);
            toast.error("Erreur lors du chargement des utilisateurs.");
        } finally {
            setLoading(false);
        }
    }

    const download = async () => {
        setIsDownloading(true);
        try {
            const resp = await downloadInterventions(sortedBy(filteredTickets));
            // Create blob link to download
            const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                'interventions_' + dateToAAAAMMJJ(null, '-') + "_" + formatTime(new Date()) + '.xlsx',
            );
            // Append to html link element page
            document.body.appendChild(link);
            // Start download
            link.click();
            // Clean up and remove the link
            link.parentNode.removeChild(link);
        } catch (err) {
            console.log(err);
        } finally {
            setIsDownloading(false);
        }
    }

    const sortedBy = ((anArray) => {
        const sortedArray = [...anArray];
        sortedArray.sort((a, b) => a.start <= b.start ? 1 : -1);

        switch (orderBy) {
            case "DATE":
                sortedArray.sort((a, b) => a.created_at <= b.created_at ? -1 : 1);
                break;
            case "-DATE":
                sortedArray.sort((a, b) => a.created_at >= b.created_at ? -1 : 1);
                break;
            case "TITLE":
                sortedArray.sort((a, b) => a.title.toLowerCase() <= b.title.toLowerCase() ? -1 : 1);
                break;
            case "-TITLE":
                sortedArray.sort((a, b) => a.title.toLowerCase() >= b.title.toLowerCase() ? -1 : 1);
                break;
            case "DESCRIPTION":
                sortedArray.sort((a, b) => a.description <= b.description ? -1 : 1);
                break;
            case "-DESCRIPTION":
                sortedArray.sort((a, b) => a.description >= b.description ? -1 : 1);
                break;
            case "PRIORITY":
                sortedArray.sort((a, b) => priority[a.priority].order <= priority[b.priority].order ? -1 : 1);
                break;
            case "-PRIORITY":
                sortedArray.sort((a, b) => priority[a.priority].order >= priority[b.priority].order ? -1 : 1);
                break;
            case "REQUESTER":
                sortedArray.sort((a, b) => (a.requester?.last_name || "") + " " + (a.requester?.first_name || "") <= (b.requester?.last_name || "") + " " + (b.requester?.first_name || "") ? -1 : 1);
                break;
            case "-REQUESTER":
                sortedArray.sort((a, b) => (a.requester?.last_name || "") + " " + (a.requester?.first_name || "") >= (b.requester?.last_name || "") + " " + (b.requester?.first_name || "") ? -1 : 1);
                break;
            case "PERFORMER":
                sortedArray.sort((a, b) => (a.performer?.last_name || "") + " " + (a.performer?.first_name || "") <= (b.performer?.last_name || "") + " " + (b.performer?.first_name || "") ? -1 : 1);
                break;
            case "-PERFORMER":
                sortedArray.sort((a, b) => (a.performer?.last_name || "") + " " + (a.performer?.first_name || "") >= (b.performer?.last_name || "") + " " + (b.performer?.first_name || "") ? -1 : 1);
                break;
            case "DURATION":
                sortedArray.sort((a, b) => getDuration(a.start_time, a.completion_time) <= getDuration(b.start_time, b.completion_time) ? -1 : 1);
                break;
            case "-DURATION":
                sortedArray.sort((a, b) => getDuration(a.start_time, a.completion_time) >= getDuration(b.start_time, b.completion_time) ? -1 : 1);
                break;
            case "STATUS":
                sortedArray.sort((a, b) => a.status <= b.status ? -1 : 1);
                break;
            case "-STATUS":
                sortedArray.sort((a, b) => a.status >= b.status ? -1 : 1);
                break;
            default:
                sortedArray.sort((a, b) => a.start <= b.start ? 1 : -1);
        }
        return sortedArray;
    })

    const setOrder = ((val) => {
        switch (val) {
            case "DATE":
                setOrderBy(orderBy === "DATE" ? "-DATE" : "DATE");
                break;
            case "TITLE":
                setOrderBy(orderBy === "TITLE" ? "-TITLE" : "TITLE");
                break;
            case "DESCRIPTION":
                setOrderBy(orderBy === "DESCRIPTION" ? "-DESCRIPTION" : "DESCRIPTION");
                break;
            case "REQUESTER":
                setOrderBy(orderBy === "REQUESTER" ? "-REQUESTER" : "REQUESTER");
                break;
            case "PERFORMER":
                setOrderBy(orderBy === "PERFORMER" ? "-PERFORMER" : "PERFORMER");
                break;
            case "DURATION":
                setOrderBy(orderBy === "DURATION" ? "-DURATION" : "DURATION");
                break;
            case "PRIORITY":
                setOrderBy(orderBy === "PRIORITY" ? "-PRIORITY" : "PRIORITY");
                break;
            case "STATUS":
                setOrderBy(orderBy === "STATUS" ? "-STATUS" : "STATUS");
                break;
            default:
                setOrderBy("DATE");
        }
    });

    const handleFilter = ((rub, val) => {
        setFilter({ ...filter, [rub]: val })
    });

    useEffect(() => {
        fetchCategories();
        fetchUsers();
        fetchTickets();
    }, []);


    // Filtrage des tickets traités sur la période
    const periodTickets = useMemo(() => {
        return tickets.filter((t) => {
            const startTime = new Date(t.start_time);
            const created = new Date(t.created_at);
            const start = new Date(startDate);
            const end = new Date(endDate);
            end.setDate(end.getDate() + 1);
            return (created >= start && created <= end) || (startTime >= start && startTime <= end);
        });
    }, [tickets, startDate, endDate]);

    // 🔹 Filtrage des tickets
    const filteredTickets = useMemo(() => {
        setCurrentPage(1);
        return periodTickets.filter((t) => {
            const matchStatus = filter.status === "ALL" || t.status === filter.status;
            const matchPriority = filter.priority === "ALL" || t.priority === filter.priority;
            const matchTeam = (filter.team === "-1" && availableTeams.some(a => a.id == t.team_id)) || t.team_id?.toString() === filter.team;
            const matchFamily = filter.family === "ALL" || t.category.family === filter.family;
            const matchCategory = filter.category === "-1" || t.category_id?.toString() === filter.category;
            const matchRequester = filter.requester === "-1" || t.requester_id?.toString() === filter.requester;
            const matchPerformer = filter.performer === "-1" || t.performer_id?.toString() === filter.performer;
            return matchStatus && matchPriority && matchTeam && matchFamily && matchCategory && matchRequester && matchPerformer;
        }).sort((a, b) => a.created_at <= b.created_at ? -1 : 1);
    }, [periodTickets, filter]);

    // 📈 Calculs
    const stats = useMemo(() => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setDate(end.getDate() + 1);
        const created = filteredTickets.filter((t) => (new Date(t.created_at) >= start && new Date(t.created_at) <= end)).length;
        const unplanned = filteredTickets.filter((t) => ["OPEN", "PENDING"].includes(t.status)).length;
        const planned = filteredTickets.filter((t) => (t.status == "ONGOING") && (new Date(t.start_time) >= start && new Date(t.start_time) <= end)).length;
        const done = filteredTickets.filter((t) => (["CLOSED", "UNREALIZABLE"].includes(t.status)) && (new Date(t.start_time) >= start && new Date(t.start_time) <= end)).length;

        const histogram = {};

        filteredTickets.filter((t) => (new Date(t.created_at) >= start && new Date(t.created_at) <= end)).forEach((t) => {
            histogram[dateToAAAAMMJJ(t.created_at, "-")] = (histogram[dateToAAAAMMJJ(t.created_at, "-")] || 0) + 1;
        })

        return { created, unplanned, planned, done, histogram };
    }, [filteredTickets]);

    // 🥧 Répartition
    const distribution = useMemo(() => {
        const map = {};

        filteredTickets.forEach((t) => {
            const key = () => {
                switch (groupBy) {
                    case "priority": return [t.priority, priority[t.priority].label, priority[t.priority].order, priority[t.priority].piecolor];
                    case "team": return [t.team.id, t.team.name, 0, t.team.bg_color];
                    case "family": return [t.category.family, t.category.family, 0, null];
                    case "category": return [t.category.id, t.category.family + " > " + t.category.label, 0, null];
                    case "requester": return [t.requester.id, (t.requester.last_name || "") + " " + (t.requester.first_name || ""), 0, null];
                    case "performer":
                        return t.performer
                            ? [t.performer.id, (t.performer?.last_name || "") + " " + (t.performer.first_name || ""), 0, null]
                            : [-1, "< non affectée >", 0, null];
                    case "status": return [t.status, status[t.status].label, status[t.status].order, status[t.status].piecolor];
                }
            };
            map[key()[0]] = {
                label: key()[1],
                value: (map[key()[0]]?.value || 0) + 1,
                order: key()[2],
                color: key()[3],
            };

        });

        return Object.keys(map).map((k) => ({
            label: map[k].label,
            value: map[k].value,
            order: map[k].order,
            color: map[k].color,
        })).sort((a, b) => { return a.order > 0 ? a.order < b.order ? -1 : 1 : a.label < b.label ? -1 : 1 });


    }, [filteredTickets, groupBy]);

    const totalPages = Math.ceil(filteredTickets.length / rowsPerPage);
    const paginatedRows = sortedBy(filteredTickets).slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    return (
        <div className="p-6 space-y-6">
            {/* En-tête */}
            <div className="flex flex-col md:flex-row justify-between flex-wrap w-full md:items-end gap-4">
                {/* <h1 className="text-2xl font-bold text-gray-800">Statistiques</h1> */}

                {/* Filtres */}
                <Filter
                    buttonLabel={
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                        </svg>

                    }
                    actualFilter={filter}
                    withLabel={true}
                    className={"flex flex-col gap-2 mb-6"}
                    listFilters={[
                        {
                            name: "status",
                            label: "Statut",
                            options: [...Object.keys(status).map(s => ({ label: status[s].label, value: s }))],
                            value: filter.status,
                        },
                        {
                            name: "priority",
                            label: "Priorité",
                            options: [...Object.keys(priority).map(p => ({ label: priority[p].label, value: p }))],
                            value: filter.priority,
                        },
                        {
                            name: "team",
                            label: "Service",
                            options: [{ label: "Tous", value: "-1" }, ...availableTeams.map(t => ({ label: t.name, value: t.id }))],
                            value: filter.team,
                        },
                        {
                            name: "family",
                            label: "Famille",
                            options: [{ label: "Toutes", value: "ALL" }, ...families.map(f => ({ label: f, value: f }))],
                            value: filter.family,
                        },
                        {
                            name: "category",
                            label: "Catégorie",
                            options: [{ label: "Toutes", value: "-1" }, ...categories.map(t => ({ label: t.family + " > " + t.label, value: t.id }))],
                            value: filter.category,
                        },
                        {
                            name: "requester",
                            label: "Créateur",
                            options: [{ label: "Tous", value: "-1" }, ...usersCreator.map(u => ({ label: (u.last_name ?? "") + " " + (u.first_name ?? ""), value: u.id }))],
                            value: filter.requester,
                        },
                        {
                            name: "performer",
                            label: "Assigné à",
                            options: [{ label: "Tous", value: "-1" }, ...usersEnroll.map(u => ({ label: (u.last_name ?? "") + " " + (u.first_name ?? ""), value: u.id }))],
                            value: filter.performer,
                        },
                    ]}
                    handleChange={handleFilter}
                />

                <div className="flex md:items-end gap-4 flex-wrap">
                    {/* flex flex-wrap justify-between w-full gap-2 */}
                    <div className="flex gap-2 items-center">
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full bg-gray-200 border rounded px-3 py-2"
                        />
                        <span className="text-gray-400">→</span>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full bg-gray-200 border rounded px-3 py-2"
                        />
                    </div>
                </div>

                <div className="flex gap-4 w-full">
                    <Button
                        id="GRAPH"
                        title="Afficher les graphiques"
                        name={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z" />
                            </svg>

                        }
                        type={display == "GRAPH" ? "" : "empty"}
                        action={() => setDisplay("GRAPH")}
                    />
                    <Button
                        id="TABLE"
                        title="Afficher la liste des interventions"
                        name={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                            </svg>

                        }
                        type={display == "TABLE" ? "" : "empty"}
                        action={() => setDisplay("TABLE")}
                    />
                    <Button
                        id="DOWNLOAD"
                        title="Télécharger"
                        name={
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                        }
                        type={""}
                        action={() => download()}
                        loading={isDownloading}
                    />


                </div>
            </div>





            {display === "GRAPH" &&
                <div>
                    {/* Statistiques principales */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <StatCard label="Nouvelles interventions" value={stats.created} color="bg-gray-100 text-app-primary" />
                        <StatCard label="Interventions non planifiées" value={stats.unplanned} color="bg-gray-100 text-app-primary" />
                        <StatCard label="Interventions planifiées" value={stats.planned} color="bg-gray-100 text-app-primary" />
                        <StatCard label="Interventions terminées" value={stats.done} color="bg-gray-100 text-app-primary" />
                    </div>
                    <div className="flex gap-4 flex-wrap lg:flex-nowrap">


                        {/* Répartition */}
                        <div className="bg-white border rounded-xl shadow-sm p-6 w-full lg:w-1/2">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold text-gray-700">
                                    Répartition des interventions par {groupByLabel[groupBy]}
                                </h2>
                                <select
                                    value={groupBy}
                                    onChange={(e) => setGroupBy(e.target.value)}
                                    className="border rounded-md text-sm px-2 py-1"
                                >
                                    <option value="priority">Par priorité</option>
                                    <option value="team">Par service</option>
                                    <option value="family">Par groupe</option>
                                    <option value="category">Par catégorie</option>
                                    <option value="requester">Par demandeur</option>
                                    <option value="performer">Par exécutant</option>
                                    <option value="status">Par statut</option>
                                </select>
                            </div>

                            {distribution.length > 0 ? (
                                <div className="flex flex-col md:flex-row items-center justify-between">
                                    <PieChart data={distribution} />
                                    <div className="space-y-2 mt-4 md:mt-0">
                                        {distribution.map((d, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm">
                                                <span className="w-3 h-3 rounded-sm border" style={{ backgroundColor: d.color === null ? randomColors[i % randomColors.length] : d.color }}></span>
                                                <span className="font-medium text-gray-500">{d.label}</span>
                                                <span className="text-gray-400">({d.value})</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-400 italic text-sm">Aucune donnée sur cette période</p>
                            )}
                        </div>

                        {/* Histogramme */}
                        <div className="bg-white border rounded-xl shadow-sm p-6 w-full lg:w-1/2">
                            <h2 className="text-lg font-semibold text-gray-700 mb-4">Interventions par date de création</h2>
                            {Object.entries(stats.histogram).length > 0 ? (
                                <Histogram data={Object.entries(stats.histogram)} />
                            ) : (
                                <p className="text-gray-400 italic text-sm">Aucune donnée sur cette période</p>
                            )}
                        </div>

                    </div>
                </div>
            }

            {
                display === "TABLE" &&

                <div className="flex border rounded-xl shadow-sm p-6 w-full flex-col justify-between">
                    {/* Affichage sur pc */}
                    <div className="hidden text-xxs lg:block xl:text-xs 2xl:text-sm">
                        {paginatedRows.length > 0 ? (
                            <div className="w-auto gap-4 flex flex-col">
                                <table className="table-fixed w-full border rounded-sm shadow-sm">
                                    <thead className="bg-gray-100 text-gray-700">
                                        <tr>
                                            <th className="px-4 py-2 text-left max-w-1/12"><span onClick={() => setOrder("DATE")} className="cursor-pointer hover:underline text-nowrap">Date de création</span></th>
                                            <th className="px-4 py-2 text-left max-w-4/12"><span onClick={() => setOrder("TITLE")} className="cursor-pointer hover:underline text-nowrap">Objet</span></th>
                                            <th className="px-4 py-2 text-left max-w-2/12"><span onClick={() => setOrder("PERFORMER")} className="cursor-pointer hover:underline text-nowrap">Assignée à</span></th>
                                            <th className="px-4 py-2 text-left max-w-2/12"><span onClick={() => setOrder("REQUESTER")} className="cursor-pointer hover:underline text-nowrap">Créateur</span></th>
                                            <th className="px-4 py-2 text-left max-w-1/12"><span onClick={() => setOrder("PRIORITY")} className="cursor-pointer hover:underline text-nowrap">Priorité</span></th>
                                            <th className="px-4 py-2 text-left max-w-1/12"><span onClick={() => setOrder("DURATION")} className="cursor-pointer hover:underline text-nowrap">Durée</span></th>
                                            <th className="px-4 py-2 text-left max-w-1/12"><span onClick={() => setOrder("STATUS")} className="cursor-pointer hover:underline text-nowrap">Etat</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedRows.map((row, index) => (
                                            <tr
                                                key={index}
                                                className="border-t hover:bg-gray-50 transition"
                                            >
                                                <td className="px-4 py-2 text-nowrap">{new Date(row.created_at).toLocaleString([], { year: '2-digit', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' })}</td>
                                                <td className="px-4 py-2 text-nowrap text-ellipsis overflow-x-clip">{row.title}</td>
                                                <td className="px-4 py-2 text-nowrap text-ellipsis overflow-x-clip">{(row.performer?.last_name || "") + " " + (row.performer?.first_name || "")}</td>
                                                <td className="px-4 py-2 text-nowrap text-ellipsis overflow-x-clip">{(row.requester?.last_name || "") + " " + (row.requester?.first_name || "")}</td>
                                                <td className="px-4 py-2">
                                                    <span className={"p-1 rounded border " + priority[row.priority].color}>{priority[row.priority].label}</span>
                                                </td>
                                                <td className="px-4 py-2">{getDuration(row.start_time, row.completion_time)}</td>
                                                <td className="px-4 py-2 text-nowrap">
                                                    <span className={"p-1 rounded border " + status[row.status].color}>{status[row.status].label}</span>
                                                </td>
                                            </tr>
                                        ))}

                                    </tbody>
                                </table>

                                {/* Pagination controls */}
                                {totalPages > 1 && (
                                    <div className="mt-2 flex justify-center items-center gap-4 text-sm">
                                        <Button
                                            name={"Précédent"}
                                            action={() =>
                                                setCurrentPage((p) => Math.max(1, p - 1))
                                            }
                                            type={currentPage === 1 ? "disabled" : "empty"}
                                            className="px-3 py-1 border rounded-sm disabled:opacity-50"
                                        />
                                        <span>
                                            Page {currentPage} sur {totalPages}
                                        </span>
                                        <Button
                                            name={"Suivant"}
                                            action={() =>
                                                setCurrentPage((p) => Math.min(totalPages, p + 1))
                                            }
                                            type={currentPage === totalPages ? "disabled" : "empty"}
                                            className="px-3 py-1 border rounded-sm disabled:opacity-50"
                                        />
                                    </div>
                                )
                                }
                            </div>
                        ) : (
                            <p className="text-gray-400 italic text-sm">Aucune donnée sur cette période</p>
                        )}
                    </div>

                    {/* Affichage sur mobile */}
                    <div className="lg:hidden flex">

                        {sortedBy(filteredTickets).length > 0 ? (
                            <div className="flex flex-col w-full gap-2">
                                {sortedBy(filteredTickets).map((ticket) => (
                                    <div
                                        key={ticket.id}
                                        className="flex flex-col justify-between rounded w-full shadow-sm p-2 pt-1 overflow-y-auto border hover:shadow-md transition relative overflow-hidden"
                                    >
                                        <div className="text-sm text-gray-600">
                                            {ticket.team && (
                                                <div
                                                    className="mb-2 h-1 -mt-1 -mx-4 rounded-full"
                                                    style={{
                                                        backgroundColor: ticket.team.bg_color,
                                                        opacity: "70%",
                                                    }}
                                                >
                                                </div>
                                            )}
                                            <div className="font-semibold text-gray-800 text-nowrap overflow-hidden text-ellipsis">{ticket.title}</div>
                                            <div className="italic text-gray-500 text-nowrap">{ticket.category.family + " > " + ticket.category.label}</div>

                                            {/* <span className="text-sm text-gray-500 mb-2 line-clamp-1">{ticket.description}</span> */}
                                            {ticket.performer &&
                                                <div>➡️  {ticket.performer.last_name} {ticket.performer.first_name}</div>
                                            }
                                            {ticket.requester &&
                                                <div>Créateur :  {ticket.requester.last_name} {ticket.requester.first_name}</div>
                                            }

                                            {ticket.start_time && (
                                                <div> {["CLOSED","UNREALIZABLE"].includes(ticket.status) ? "Terminée" : "Prévue"} le {new Date(ticket.start_time).toLocaleDateString()} à {new Date(ticket.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            )}
                                            {ticket.due_date && !["CLOSED","UNREALIZABLE"].includes(ticket.status) && (
                                                <div style={ticket.due_date && (new Date(ticket.due_date) < new Date()) ? { fontWeight: "bolder", color: "#ff2222" } : {}} > Échéance : {new Date(ticket.due_date).toLocaleDateString()}</div>
                                            )}
                                            {getDuration(ticket.start_time, ticket.completion_time, "HC") > 0 &&
                                                <div>Durée : {getDuration(ticket.start_time, ticket.completion_time)}</div>
                                            }
                                        </div>

                                        <div className="flex justify-between items-center mt-4">
                                            <span className={`text-xs px-2 py-1 rounded-full border ${priority[ticket.priority].color}`}>
                                                {priority[ticket.priority].label}
                                            </span>
                                            <span className={`text-xs px-2 py-1 rounded-full border ${status[ticket.status].color}`}>
                                                {status[ticket.status].label}
                                            </span>
                                        </div>

                                    </div>
                                ))}

                            </div>
                        ) : (
                            <p className="text-gray-400 italic text-sm">Aucune donnée sur cette période</p>
                        )}
                    </div>
                </div>
            }

        </div >
    );
}

/* --------------------------------------------------------------------- */
/* 🧩 Composant carte de statistique */
function StatCard({ label, value, color }) {
    return (
        <div className={`rounded-xl shadow-sm border p-4 text-center ${color}`}>
            <div className="text-sm font-medium">{label}</div>
            <div className="text-2xl font-bold mt-1">{value}</div>
        </div>
    );
}

/* --------------------------------------------------------------------- */
/* 📊 Histogramme SVG */
function Histogram({ data }) {
    const max = Math.max(...data.map((d) => d[1]), 1);
    const barWidth = 100 / data.length;
    return (
        <div className="w-full h-64">
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                {data.map((d, i) => {
                    const height = (d[1] / max) * 90;
                    return (
                        <rect
                            key={d[0]}
                            x={i * barWidth + 2}
                            y={100 - height - 5}
                            width={barWidth - 4}
                            height={height}
                            rx="1"
                            className="fill-blue-500 opacity-80 hover:opacity-100 transition-all"
                        >
                            <title>{d[1]}</title>
                        </rect>
                    );
                })}
                <line x1="0" y1="95" x2="100" y2="95" stroke="#ccc" strokeWidth="0.5" />
            </svg>
            <div className="relative bottom-5 left-0 right-0 flex justify-around text-xs text-gray-500 px-2 pt-2">
                {data.map((d) => (
                    <span key={d[0]} className="text-center">
                        {new Date(d[0]).toLocaleDateString([], { month: "numeric", day: "numeric" })}<br /><span className="text-gray-400">({d[1]})</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

/* --------------------------------------------------------------------- */
/* 🥧 Camembert SVG */
function PieChart({ data }) {
    const total = data.reduce((a, b) => a + b.value, 0);
    let cumulative = 0;
    return (
        <svg viewBox="0 0 36 36" className="w-64 h-64">
            {data.map((slice, i) => {
                const startAngle = (cumulative / total) * 360;
                const endAngle = ((cumulative + slice.value) / total) * 360;
                cumulative += slice.value;

                const largeArc = endAngle - startAngle > 180 ? 1 : 0;
                const x1 = 18 + 16 * Math.cos((Math.PI * startAngle) / 180);
                const y1 = 18 + 16 * Math.sin((Math.PI * startAngle) / 180);
                const x2 = 18 + 16 * Math.cos((Math.PI * (startAngle == 0 && endAngle == 360 ? 359.999 : endAngle)) / 180);
                const y2 = 18 + 16 * Math.sin((Math.PI * (startAngle == 0 && endAngle == 360 ? 359.999 : endAngle)) / 180);

                const d = `M18,18 L${x1},${y1} A16,16 0 ${largeArc} 1 ${x2},${y2} Z`;

                return (
                    <path
                        key={i}
                        d={d}
                        fill={slice.color === null ? randomColors[i % randomColors.length] : slice.color}
                        className="opacity-80 hover:opacity-100 transition"
                    />
                );
            })}
        </svg>
    );
}
